export type TerminalProviderKey = string;

export interface TerminalProfileRef {
  providerKey: TerminalProviderKey;
  profileKey: string;
  profileVersion: string;
}

export interface TerminalCapabilities {
  completionMode: 'online-authorized-intent' | 'final-confirmation-required' | null;
  postAuthentication: boolean;
  fileRetrieval: boolean;
  messageDisplay: boolean;
  multiGate: boolean;
  localValidation: boolean;
}

export interface TerminalProfile {
  key: string;
  version: string;
  model: string;
  firmware: string;
  qualified: boolean;
  capabilities: TerminalCapabilities;
}

export type TerminalIngressKind = 'online' | 'batch' | 'keepalive';

export interface TerminalRawRequest {
  kind: TerminalIngressKind;
  method: 'GET' | 'POST';
  rawQuery: string;
  body?: Uint8Array;
}

export type TerminalEventOutcome = 'provisional' | 'final-success' | 'final-incomplete' | 'denied';

export type TerminalEventIntent = 'clock' | 'balance' | 'absence' | 'unsupported';

export interface TerminalAttemptEvent {
  intent: TerminalEventIntent;
  rawWallDate: string;
  rawWallTime: string;
  rawCounter?: string;
  direction: 'entry' | 'exit' | null;
  rawDirection: string;
  rawReason: string;
  badge: string;
  rawControls: string;
  rawResult: string;
  rawSource: string;
  gate?: string;
  rawExtensions: readonly string[];
  outcome: TerminalEventOutcome;
  identityFields: Readonly<Record<string, string>>;
}

export type TerminalDeliveryAck = 'ok' | 'error';

export interface TerminalKeepaliveAcks {
  file?: TerminalDeliveryAck;
  cmd?: TerminalDeliveryAck;
}

export type TerminalIngressAttempt = (
  | {
      kind: 'keepalive';
      logicalId: string;
      deviceDate?: string;
      deviceTime?: string;
      pendingLocalCount: number | null;
      acks: TerminalKeepaliveAcks;
    }
  | { kind: 'online' | 'batch'; logicalId: string; event: TerminalAttemptEvent }
  | { kind: 'invalid'; logicalId?: string; reason: string }
) & { observed: Readonly<Record<string, string>> };

export type TerminalDisplayLocale = 'de' | 'en' | 'fr' | 'it';

export type TerminalReplyIntent =
  | { kind: 'online-deny'; notice: 'stored' | 'not-booked' | 'balance-unavailable'; locale?: TerminalDisplayLocale }
  | { kind: 'online-accept'; lines: readonly string[] }
  | { kind: 'batch-ack' }
  | { kind: 'keepalive-files'; files: readonly TerminalFile[] }
  | { kind: 'keepalive-command'; command: string }
  | { kind: 'empty' };

export interface TerminalFile {
  name: string;
  content: string;
}

export interface TerminalEncodedReply {
  contentType: string;
  body: string;
}

export interface TerminalEventIdentity {
  scheme: 'sequence-epoch' | 'fingerprint-v1';
  key: string;
}

export interface TerminalProvider {
  readonly key: TerminalProviderKey;
  readonly profiles: readonly TerminalProfile[];
  identifyProfile(observed: { model?: string; firmware?: string; reader?: string }): TerminalProfile | null;
  parse(request: TerminalRawRequest, profile: TerminalProfile): TerminalIngressAttempt;
  encodeReply(intent: TerminalReplyIntent, profile: TerminalProfile): TerminalEncodedReply;
  eventIdentity(terminalId: string, event: TerminalAttemptEvent, profile: TerminalProfile): TerminalEventIdentity;
}
